import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { FileState } from '../interfaces/file-state.interface';

export interface FileRemovedEvent {
    id?: string;
    name?: string;
}

@Injectable({
    providedIn: 'root',
})
export class FileStateService {
    private readonly _files = signal<FileState[]>([]);
    readonly files = computed(() => this._files());
    private readonly _fileRemoved$ = new Subject<FileRemovedEvent>();
    readonly fileRemoved$ = this._fileRemoved$.asObservable();

    readonly _filesFromCurrentConversation = signal<FileState[]>([]);
    readonly isFileIntoConversation = computed(
        () => this._filesFromCurrentConversation().length > 0
    );

    private readonly _myUploads = signal<number>(0);

    myUploads = this._myUploads.asReadonly();

    private readonly _showConversationFiles = signal<boolean>(false);
    readonly showConversationFiles = computed(() => this._showConversationFiles());

    /**
     *
     * @param newFiles
     */
    addConversationFiles(newFiles: FileState[]): void {
        const current = this._filesFromCurrentConversation();
        const updated = [...current, ...newFiles];
        this._filesFromCurrentConversation.set(updated);
    }

    /**
     *
     */
    clearConversationFiles(): void {
        this._filesFromCurrentConversation.set([]);
    }

    /**
     *
     */
    getConversationFiles(): FileState[] {
        return this._filesFromCurrentConversation();
    }

    /**
     *
     * @param newFiles - An array of `File` objects to be added to the current file state.
     */
    addFiles(newFiles: FileState[]): void {
        const current = this._files();
        const updated = [...current, ...newFiles];
        this._files.set(updated);
    }

    /**
     *
     * @param fileName
     * @param fileId
     */
    removeFileById(fileId: string): void {
        this.removeFile(fileId);
    }

    /**
     * Removes a file from local state using id and/or name matching.
     * This is needed when UI state keeps a local id while backend operations return a server id.
     * @param id
     * @param name
     */
    removeFile(id?: string, name?: string): void {
        if (!id && !name) {
            return;
        }

        const normalizedName = name?.trim().toLowerCase();
        const previous = this._files();
        const removedFiles = previous.filter((item: FileState) => {
            const fileName = item?.name?.trim().toLowerCase();
            const matchesId = !!id && item.id === id;
            const matchesName = !!normalizedName && fileName === normalizedName;
            return matchesId || matchesName;
        });

        if (!removedFiles.length) {
            return;
        }

        const removedSet = new Set(removedFiles);
        const filtered = previous.filter((item: FileState) => !removedSet.has(item));
        this._files.set(filtered);

        removedFiles.forEach((file) => {
            this._fileRemoved$.next({ id: file?.id || id, name: file?.name || name });
        });
    }

    /**
     * Notifies listeners that a file was removed externally (for example, from My Uploads).
     * This keeps already-rendered chat messages in sync even when local attachment state did not change.
     * @param id
     * @param name
     */
    notifyFileRemoved(id?: string, name?: string): void {
        if (!id && !name) {
            return;
        }

        this._fileRemoved$.next({ id, name });
    }

    /**
     *
     */
    clearFiles(): void {
        this._files.set([]);
    }

    /**
     *
     */
    getFiles(): FileState[] {
        return this._files();
    }

    /**
     *

     * @param value
     */
    setShowConversationFiles(value: boolean) {
        this._showConversationFiles.set(value);
    }

    /**
     *
     */
    getMyUploads() {
        return this._myUploads();
    }

    /**
     *
     * @param uploadCount
     */
    setMyUploads(uploadCount) {
        this._myUploads.set(uploadCount);
    }
}
